import { useState, useEffect, useCallback } from "react";
import * as LocalAuthentication from "expo-local-authentication";
import { Platform } from "react-native";

export type BiometryType = "Face ID" | "Fingerprint" | "Iris" | "Face Recognition" | "Touch ID" | "Biometric";

interface UseBiometricAuthReturn {
  isAvailable: boolean;
  biometryType: BiometryType | null;
  isLoading: boolean;
  hasHardware: boolean;
  isEnrolled: boolean;
  authenticate: (promptMessage?: string) => Promise<boolean>;
}

export const useBiometricAuth = (): UseBiometricAuthReturn => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState<BiometryType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasHardware, setHasHardware] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);

  const checkBiometricAvailability = useCallback(async () => {
    try {
      setIsLoading(true);

      // Check if hardware is available
      const hasHardwareSupport = await LocalAuthentication.hasHardwareAsync();
      setHasHardware(hasHardwareSupport);

      if (!hasHardwareSupport) {
        setIsAvailable(false);
        setIsLoading(false);
        return;
      }

      // Check if biometric records are enrolled
      const isEnrolledResult = await LocalAuthentication.isEnrolledAsync();
      setIsEnrolled(isEnrolledResult);

      // Get supported authentication types
      const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();
      let detectedBiometryType: BiometryType | null = null;

      if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
        detectedBiometryType = Platform.OS === "android" ? "Face Recognition" : "Face ID";
      }

      if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
        detectedBiometryType = Platform.OS === "android" ? "Fingerprint" : "Touch ID";
      }

      if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
        detectedBiometryType = "Iris";
      }

      setBiometryType(detectedBiometryType);

      // Available if we have hardware and enrolled biometrics
      setIsAvailable(hasHardwareSupport && isEnrolledResult && supportedTypes.length > 0);
    } catch (error) {
      console.error("Error checking biometric availability:", error);
      setIsAvailable(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const authenticate = useCallback(
    async (promptMessage = "Authenticate to continue"): Promise<boolean> => {
      try {
        if (!isAvailable) {
          return false;
        }

        const result = await LocalAuthentication.authenticateAsync({
          promptMessage,
          disableDeviceFallback: false, // Allow device PIN/passcode as fallback
          fallbackLabel: "Use Passcode", // Custom fallback button text
        });

        return result.success;
      } catch (error) {
        console.error("Biometric authentication error:", error);
        return false;
      }
    },
    [isAvailable],
  );

  useEffect(() => {
    checkBiometricAvailability();
  }, [checkBiometricAvailability]);

  return {
    isAvailable,
    biometryType,
    isLoading,
    hasHardware,
    isEnrolled,
    authenticate,
  };
};
